/**
 * Lift2Fit — coaching workspace (CRM) shared vocabulary.
 *
 * Small, dependency-free helpers the CRM screens share: local-time week maths
 * for the schedule, money and flag labels, and the one status colour system
 * (colour + glyph + word, never colour alone). Nothing here talks to the API.
 */
import type {
  ReactNode } from 'react';
import type {
  CrmBookingStatus,
  CrmInsightSeverity,
  CrmPaymentStatus,
  CrmSlotKind,
  CrmVerdict,
  ManagedBy,
} from '@/lib/api';
import { cn } from '@/lib/format';
import { ArrowRightIcon } from '@/lib/icons';
import type { ChipTone } from '../components';
import { Link } from '../router';

/**
 * The eyebrow every workspace screen carries, so the two products read apart.
 * A coaching business is one entity — a solo coach or a club of eight is a
 * size question, never a different product.
 */
export const CRM_EYEBROW = 'Coaching workspace';

/**
 * 0034 — whose client is this, and who delivers them, in one sentence.
 *
 * A gym and an internal coach can both be looking at the same client, and the
 * two facts are different: OWNERSHIP (whose book the client is in) and DELIVERY
 * (who manages them day to day). The API sends both as `youOwn`/`youManage`, so
 * this one helper words every screen — the clients list, the client page and the
 * chat panel — and they cannot drift apart:
 *
 *   your client, you deliver        "Your client"
 *   your client, a coach delivers   "Your client · Pat Trainer manages"
 *   the gym's client, you deliver   "Iron House Gym's client · you manage"
 *   the gym's client, they deliver  "Iron House Gym's client · Pat Trainer manages"
 */
export function managedBySentence(managed: ManagedBy | null | undefined): string | null {
  if (!managed) return null;
  if (managed.youOwn && managed.youManage) return 'Your client';
  if (managed.youOwn) return `Your client · ${managed.displayName} manages`;
  if (managed.youManage) return `${managed.ownerName}'s client · you manage`;
  return `${managed.ownerName}'s client · ${managed.displayName} manages`;
}

/* ---------------------------------------------------------------------------
   Local-time maths. The API stores UTC instants; a coach thinks in local days,
   so the week window and the day grouping are computed locally and only the
   range boundaries are converted back to ISO for the query.
   ------------------------------------------------------------------------ */

const DAY_MS = 86_400_000;

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

/** Monday 00:00 local of the ISO week containing `date`. */
export function startOfIsoWeek(date: Date): Date {
  const day = startOfLocalDay(date);
  const dow = day.getDay(); // 0 = Sunday
  return addDays(day, dow === 0 ? -6 : 1 - dow);
}

export function isSameLocalDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
  );
}

/** `YYYY-MM-DD` in local time, the value an `<input type="date">` wants. */
export function toDateInput(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/** `YYYY-MM-DDTHH:mm` in local time, the value a `datetime-local` wants. */
export function toDateTimeInput(date: Date): string {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${toDateInput(date)}T${hours}:${minutes}`;
}

/** Parse a `datetime-local` value into an ISO instant, or null when unusable. */
export function fromDateTimeInput(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export function formatClock(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

export function formatSlotRange(startIso: string, endIso: string): string {
  return `${formatClock(startIso)} – ${formatClock(endIso)}`;
}

export function formatDayHeading(date: Date): string {
  return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function formatDayLong(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

export function formatDateShort(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Whole days between an instant and today, local, floored at zero. */
export function daysUntil(iso: string, now: Date = new Date()): number | null {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return Math.round((startOfLocalDay(date).getTime() - startOfLocalDay(now).getTime()) / DAY_MS);
}

// The one exact-money formatter lives in `@/lib/format` beside its compact
// sibling, so a summary tile and the ledger it points at can never drift.
export { formatMoney } from '@/lib/format';

/* ---------------------------------------------------------------------------
   Status vocabulary — always colour + word.
   ------------------------------------------------------------------------ */

const TONES: Record<ChipTone, string> = {
  neutral: 'text-ink-muted',
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  info: 'text-info',
};

/** An announced async outcome. `danger` is a refusal the coach must read. */
export function CrmMessage({
  message,
  tone = 'info',
  testId = 'crm-message',
}: {
  message: string | null;
  tone?: ChipTone;
  testId?: string;
}) {
  return (
    <p
      data-testid={testId}
      role={tone === 'danger' ? 'alert' : undefined}
      aria-live="polite"
      className={cn('min-h-[20px] text-[13px]', TONES[tone])}
    >
      {message ?? ''}
    </p>
  );
}

/** A refusal straight from the API — set apart so the exact words are read. */
export function RefusalNotice({
  detail,
  className,
  testId,
}: {
  detail: string | null;
  className?: string;
  testId?: string;
}) {
  if (!detail) return null;
  return (
    <p
      data-testid={testId}
      role="alert"
      className={cn('border-l-2 border-danger bg-page-subtle px-3 py-2 text-[13px] text-danger', className)}
    >
      {detail}
    </p>
  );
}

const FLAG_LABELS: Record<string, string> = {
  missed_sessions: 'Missed sessions',
  adherence_drop: 'Adherence drop',
  pain_reported: 'Pain reported',
  rpe_creep: 'RPE creep',
  nothing_scheduled: 'Nothing scheduled',
  no_recent_pr: 'No recent PR',
  review_due: 'Review due',
  fatigue: 'Fatigue',
  stalled: 'Stalled',
  deload_recommended: 'Deload recommended',
};

export function crmFlagLabel(code: string): string {
  return (
    FLAG_LABELS[code] ??
    code.replace(/_/g, ' ').replace(/^\w/, (character) => character.toUpperCase())
  );
}

export function severityTone(severity: CrmInsightSeverity): ChipTone {
  if (severity === 'danger') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'info';
}

export function severityWord(severity: CrmInsightSeverity): string {
  if (severity === 'danger') return 'Action';
  if (severity === 'warning') return 'Watch';
  return 'Note';
}

export function bookingStatusMeta(status: CrmBookingStatus): { label: string; tone: ChipTone } {
  switch (status) {
    case 'attended':
      return { label: 'Attended', tone: 'success' };
    case 'no_show':
      return { label: 'No-show', tone: 'danger' };
    case 'cancelled':
      return { label: 'Cancelled', tone: 'neutral' };
    default:
      return { label: 'Booked', tone: 'info' };
  }
}

export function paymentStatusMeta(status: CrmPaymentStatus): { label: string; tone: ChipTone } {
  switch (status) {
    case 'paid':
      return { label: 'Paid', tone: 'success' };
    case 'refunded':
      return { label: 'Refunded', tone: 'neutral' };
    default:
      return { label: 'Due', tone: 'warning' };
  }
}

export function verdictMeta(verdict: CrmVerdict): { label: string; tone: ChipTone } {
  switch (verdict) {
    case 'progressing_fast':
      return { label: 'Progressing fast', tone: 'success' };
    case 'progressing':
      return { label: 'Progressing', tone: 'success' };
    case 'on_track':
      return { label: 'On track', tone: 'success' };
    case 'stalled':
      return { label: 'Stalled', tone: 'warning' };
    case 'deload_recommended':
      return { label: 'Deload recommended', tone: 'warning' };
    case 'overreaching':
      return { label: 'Overreaching', tone: 'danger' };
    default:
      return { label: 'Not enough data', tone: 'neutral' };
  }
}

export function slotKindLabel(kind: CrmSlotKind): string {
  return kind === 'group' ? 'Group' : '1-to-1';
}

/**
 * The plan a workspace is on, described by how many clients and internal
 * coaches it covers. Coach is for managing your own clients; the Gyms tiers add
 * internal coaches on top of the client ceiling.
 */
export function planSizeLabel(plan: 'free' | 'coach' | 'gym' | 'gym_plus' | 'scale'): string {
  switch (plan) {
    case 'coach':
      return 'up to 10 clients · own clients only';
    case 'gym':
      return 'up to 20 clients · 10 internal coaches';
    case 'gym_plus':
      return 'up to 100 clients · 20 internal coaches';
    case 'scale':
      return 'unlimited clients · negotiated';
    default:
      // 0034: the free plan has no coaching workspace at all.
      return 'no coaching workspace · coaching is paid';
  }
}

export function seatLimitLabel(limit: number | null): string {
  return limit === null ? 'unlimited' : String(limit);
}

export function insightSourceLabel(source: 'evaluation_matrix' | 'roster'): string {
  return source === 'evaluation_matrix' ? 'Evaluation matrix' : 'Roster facts';
}

/** A tiny labelled stat used inside cards. */
export function MiniStat({
  label,
  value,
  tone = 'text-ink',
  valueTitle,
}: {
  label: string;
  value: ReactNode;
  tone?: string;
  valueTitle?: string;
}) {
  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-ink-muted">{label}</span>
      <span title={valueTitle} className={cn('l2f-tabular min-w-0 break-words font-mono text-[14px] font-bold', tone)}>
        {value}
      </span>
    </span>
  );
}

/* ---------------------------------------------------------------------------
   Bounded lists.

   A coaching book grows without asking the layout's permission, so every list
   that can grow says how many rows it is holding back and gives one way to
   reach the rest. The panel keeps a predictable height; the count is the
   honest total, never the truncated slice.
   ------------------------------------------------------------------------ */

/** Summary cards show five rows — enough to act on, short enough to scan. */
export const SUMMARY_LIST_LIMIT = 5;
/** A full-page queue shows eight rows — a page, not a wall. */
export const QUEUE_LIST_LIMIT = 8;

/**
 * The honest tail of a bounded list.
 *
 * `total` is the real number of rows (from the API or the full client-side
 * array), `shown` is how many are rendered, so `+N more` is always truthful.
 * Exactly one way forward is offered: `to` for a list whose fuller surface is
 * another screen, or `onShowAll` where this list IS that surface and expands in
 * place. When neither is given the footer still reports the hidden count.
 */
export function ListMore({
  total,
  shown,
  to,
  toLabel = 'View all',
  onShowAll,
  testId,
  className,
}: {
  total: number;
  shown: number;
  to?: string;
  toLabel?: string;
  onShowAll?: () => void;
  testId: string;
  className?: string;
}) {
  const hidden = Math.max(0, total - shown);
  if (hidden <= 0) return null;
  const actionClass =
    'inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-accent hover:text-accent-hover';
  return (
    <div
      data-testid={testId}
      className={cn('flex flex-wrap items-center justify-between gap-2 border-t border-hairline pt-3', className)}
    >
      <span className="text-[12px] text-ink-muted">
        Showing {shown} of {total} · +{hidden} more
      </span>
      {to ? (
        <Link to={to} data-testid={`${testId}-link`} className={actionClass}>
          {toLabel} ({total})
          <ArrowRightIcon />
        </Link>
      ) : onShowAll ? (
        <button type="button" data-testid={`${testId}-button`} onClick={onShowAll} className={actionClass}>
          {toLabel} ({total})
          <ArrowRightIcon />
        </button>
      ) : null}
    </div>
  );
}
